import React, { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Divider, Grid, Paper, Tooltip, Typography } from '@material-ui/core';
import { DatePickers, Loaders } from 'components';

import { useUserActivityStatsQuery } from 'api';
import { useCurrentUser, useDateRange, useNotify } from 'hooks';

import { LINE_CHART_HEIGHT } from '../../constants';
import LineChart from './LineChart';
import Value from './Value';
import Change from './Change';
import LineChartEmptyState from './LineChartEmptyState';

const ActivityWithImpressions: FC = () => {
  const currentUser = useCurrentUser();
  const notify = useNotify();
  const { t } = useTranslation();
  const { startDate, setStartDate, endDate, setEndDate, dateRange, setDateRange } = useDateRange();

  const { data, isLoading } = useUserActivityStatsQuery(
    { user_id: currentUser.id, start_date: startDate, end_date: endDate },
    {
      onError: notify.queryError,
    }
  );

  const { userActivityStats } = data || {};
  const { daily_stats = [], percent_change = {}, summary_stats = {} } = userActivityStats || {};

  function renderChart(dataKey: string, label: string) {
    if (isLoading) {
      return <Loaders.LineChart height={LINE_CHART_HEIGHT} />;
    }

    if (!isLoading && !daily_stats.length) {
      return <LineChartEmptyState text={t('emptyStates:activityStatChart')} />;
    }

    return <LineChart data={daily_stats} config={{ dataKey, label }} />;
  }

  function renderItem(dataKey: string, label: string) {
    return (
      <Fragment>
        <Tooltip title={t(`help:activity_${dataKey}`)}>
          <Typography display="inline" variant="subtitle2" color="textSecondary">
            {label}
          </Typography>
        </Tooltip>
        <Grid container alignItems="flex-end" spacing={1}>
          <Grid item>
            <Value isLoading={isLoading} value={summary_stats[dataKey]} />
          </Grid>
          <Grid item>
            <Change isLoading={isLoading} value={percent_change[dataKey]} />
          </Grid>
        </Grid>
        <Box marginTop={2} position="relative">
          {renderChart(dataKey, label)}
        </Box>
      </Fragment>
    );
  }

  return (
    <Paper>
      <Box paddingLeft={3} paddingTop={3} marginBottom={2}>
        <Typography variant="h6">
          {t(isLoading ? 'help:activity_external_shares' : 'components:myShares')}
        </Typography>
      </Box>
      <Box paddingLeft={3} paddingRight={3} paddingBottom={2}>
        <Grid container spacing={3}>
          <Grid item xs={3}>
            {renderItem('shares', t('common:share_plural'))}
          </Grid>
          <Grid item xs={3}>
            {renderItem('impressions', t('common:impression_plural'))}
          </Grid>
          <Grid item xs={3}>
            {renderItem('clicks', t('common:click_plural'))}
          </Grid>
          <Grid item xs={3}>
            {renderItem('engagements', t('common:engagement_plural'))}
          </Grid>
        </Grid>
      </Box>
      <Divider />
      <Box paddingLeft={3} paddingTop={3} marginBottom={2}>
        <Typography variant="h6">
          {t(isLoading ? 'help:activity_internal_posts' : 'components:myActivity')}
        </Typography>
      </Box>
      <Box paddingLeft={3} paddingRight={3} paddingBottom={2}>
        <Grid container spacing={3}>
          <Grid item xs={4}>
            {renderItem('posts', t('common:post_plural'))}
          </Grid>
          <Grid item xs={4}>
            {renderItem('reactions', t('common:like_plural'))}
          </Grid>
          <Grid item xs={4}>
            {renderItem('comments', t('common:comment_plural'))}
          </Grid>
        </Grid>
      </Box>
      <Divider />
      <Box paddingLeft={1}>
        <DatePickers.Range
          dateRange={dateRange}
          startDate={startDate}
          endDate={endDate}
          onSetStart={setStartDate}
          onSetEnd={setEndDate}
          onSetRange={setDateRange}
        />
      </Box>
    </Paper>
  );
};

export default ActivityWithImpressions;
